import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { Box, MenuItem, Select, Typography, useTheme } from "@mui/material";
import { forwardRef, useImperativeHandle, useMemo, useRef, useState } from "react";
import { getM3Colors } from "../../theme/style/m3Palette";
import { getOrgTokens } from "../../theme/style/organizationPalette";

/**
 * Material 3 Cell Editor for AG Grid Dropdowns.
 * Replaces unstyled native <select> with a polished, high-contrast M3 menu.
 *
 * Props passed by AG Grid:
 * @param {any} value - Initial cell value
 * @param {Array<string|{value: string, label: string, tone?: string}>} values - Available options
 * @param {Object} tones - Optional map of option value to tone ('primary'|'secondary'|'info'|'warning'|'success'|'neutral')
 * @param {function} stopEditing - AG Grid stop editing callback
 * @param {function} onValueChange - AG Grid v32 value proxy notification
 * @param {object} api - AG Grid API
 */
const M3GridSelectEditor = forwardRef((props, ref) => {
  const { value: startValue, stopEditing, api, values = [], tones = {}, onValueChange } = props;
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const m3 = getM3Colors(isDark);
  const tok = getOrgTokens(isDark);

  const initialVal = startValue != null ? String(startValue).trim() : "";
  const [value, setValue] = useState(initialVal);
  const valueRef = useRef(initialVal);
  const selectRef = useRef(null);

  // Normalize options array
  const optionList = useMemo(() => {
    const list = Array.isArray(values) ? [...values] : [];
    // Ensure current custom value is included at the top if not in the standard list
    if (initialVal && !list.some((v) => (typeof v === "object" ? v.value : v) === initialVal)) {
      list.unshift({
        value: initialVal,
        label: `${initialVal} (Custom)`,
        isCustom: true,
      });
    }
    return list;
  }, [values, initialVal]);

  useImperativeHandle(ref, () => ({
    getValue() {
      return valueRef.current;
    },
    afterGuiAttached: () => {
      setTimeout(() => {
        const input = selectRef.current?.querySelector("input") || selectRef.current;
        input?.focus();
      }, 40);
    },
  }));

  const onChange = (e) => {
    const newValue = e.target.value;
    valueRef.current = newValue;
    setValue(newValue);
    onValueChange?.(newValue);

    const stop = stopEditing || api?.stopEditing;
    if (stop) stop();
  };

  const getToneColor = (tone) => {
    switch (tone) {
      case "primary": return isDark ? m3.primary : "#659d33";
      case "info": return isDark ? m3.info : "#0284c7";
      case "success": return isDark ? m3.success : "#16a34a";
      case "warning": return isDark ? m3.warning : "#d97706";
      case "tertiary": return isDark ? m3.tertiary : "#b45309";
      case "error": return isDark ? m3.error : "#dc2626";
      case "neutral":
      default:
        return isDark ? "#9ca3af" : "#6b7280";
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        bgcolor: isDark ? "rgba(164, 213, 119, 0.08)" : "rgba(101, 157, 51, 0.06)",
        borderRadius: "8px",
      }}
    >
      <Select
        ref={selectRef}
        value={value}
        onChange={onChange}
        defaultOpen={true}
        size="small"
        fullWidth
        IconComponent={KeyboardArrowDownIcon}
        MenuProps={{
          autoFocus: true,
          disableAutoFocusItem: false,
          PaperProps: {
            sx: {
              minWidth: 400,
              maxWidth: 500,
              maxHeight: 460,
              borderRadius: "14px",
              bgcolor: isDark ? "#1e201d" : "#ffffff",
              border: `1px solid ${isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.1)"}`,
              boxShadow: isDark
                ? "0 12px 40px rgba(0, 0, 0, 0.6)"
                : "0 10px 32px rgba(0, 0, 0, 0.12)",
              padding: "6px 4px",
              "& .MuiList-root": {
                padding: "2px 0",
              },
            },
          },
        }}
        sx={{
          width: "100%",
          height: "100%",
          fontFamily: "Poppins, sans-serif",
          fontSize: "13.5px",
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: isDark ? m3.primary : "#16a34a",
            borderWidth: "1.5px",
            borderRadius: "8px",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: isDark ? m3.primary : "#16a34a",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: isDark ? m3.primary : "#16a34a",
            borderWidth: "2px",
          },
          "& .MuiSelect-select": {
            display: "flex",
            alignItems: "center",
            paddingLeft: "12px",
            paddingRight: "28px !important",
            fontFamily: "Poppins, sans-serif",
            fontSize: "13.5px",
            fontWeight: 500,
            color: m3.onSurface,
          },
          "& .MuiSelect-icon": {
            color: isDark ? m3.primary : "#16a34a",
            right: "8px",
          },
        }}
      >
        {optionList.map((item, i) => {
          const isObj = typeof item === "object" && item !== null;
          const val = isObj ? item.value : item;
          const label = isObj ? item.label : item;
          const tone = isObj ? item.tone : (tones[val] || "neutral");
          const isSelected = val === value;
          const isOthers = val === "Others (Editable)";

          return (
            <MenuItem
              key={i}
              value={val}
              sx={{
                fontFamily: "Poppins, sans-serif",
                fontSize: "13.5px",
                fontWeight: isSelected ? 600 : 400,
                color: isSelected
                  ? (isDark ? m3.primary : "#16a34a")
                  : (isDark ? "#f3f0ed" : "#1b1c1a"),
                borderRadius: "10px",
                margin: "2px 4px",
                padding: "10px 14px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1.5,
                whiteSpace: "normal",
                transition: "all 0.15s ease",
                bgcolor: isSelected
                  ? (isDark ? "rgba(164, 213, 119, 0.16)" : "rgba(22, 163, 74, 0.1)")
                  : "transparent",
                "&:hover": {
                  bgcolor: isDark
                    ? "rgba(255, 255, 255, 0.08)"
                    : "rgba(22, 163, 74, 0.08)",
                },
                "&.Mui-selected": {
                  bgcolor: isDark
                    ? "rgba(164, 213, 119, 0.18)"
                    : "rgba(22, 163, 74, 0.12)",
                  "&:hover": {
                    bgcolor: isDark
                      ? "rgba(164, 213, 119, 0.24)"
                      : "rgba(22, 163, 74, 0.18)",
                  },
                },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: getToneColor(tone),
                    flexShrink: 0,
                  }}
                />
                <Typography
                  sx={{
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "13.5px",
                    fontWeight: isSelected ? 600 : 400,
                    lineHeight: 1.4,
                  }}
                >
                  {label}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0, ml: 1 }}>
                {isOthers && (
                  <EditOutlinedIcon
                    sx={{
                      fontSize: 16,
                      color: isDark ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.45)",
                    }}
                  />
                )}
                {isSelected && (
                  <CheckRoundedIcon
                    sx={{
                      fontSize: 18,
                      color: isDark ? m3.primary : "#16a34a",
                    }}
                  />
                )}
              </Box>
            </MenuItem>
          );
        })}
      </Select>
    </Box>
  );
});

export default M3GridSelectEditor;

